<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
    content: { type: String, default: '' },
    contentType: { type: String, default: 'other' },
    isEditing: { type: Boolean, default: false },
    editableContent: { type: String, default: '' },
    contentTypeLabels: { type: Object, default: () => ({}) },
    contentTypeColors: { type: Object, default: () => ({}) },
    imageUrl: { type: String, default: null },
    imageGenerating: { type: Boolean, default: false },
    isGenerating: { type: Boolean, default: false },
    generatingStep: { type: String, default: '' },
});

const emit = defineEmits(['update:editable-content']);
const isDownloadingImage = ref(false);

const inferImageExtension = (blob) => {
    switch (blob.type) {
        case 'image/jpeg':
            return 'jpg';
        case 'image/png':
            return 'png';
        case 'image/webp':
            return 'webp';
        case 'image/gif':
            return 'gif';
        default:
            return 'png';
    }
};

const inferImageFilename = (imageUrl, blob) => {
    try {
        const url = new URL(imageUrl, window.location.origin);
        const lastSegment = url.pathname.split('/').pop();
        if (lastSegment && lastSegment.includes('.')) {
            return lastSegment;
        }
    } catch (e) {
        // Ignore malformed URLs and fall back to a generated filename.
    }

    return `generated-image.${inferImageExtension(blob)}`;
};

const downloadImage = async () => {
    if (!props.imageUrl || isDownloadingImage.value) return;

    isDownloadingImage.value = true;

    try {
        const response = await fetch(props.imageUrl);
        if (!response.ok) {
            throw new Error(`Failed to download image: ${response.status}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = inferImageFilename(props.imageUrl, blob);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
    } catch (e) {
        console.error('Failed to download generated image', e);
    } finally {
        isDownloadingImage.value = false;
    }
};

const formattedContent = computed(() => {
    if (!props.content) return '';

    let html = props.content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    if (props.contentType === 'blog' || props.contentType === 'newsletter') {
        html = html
            .replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
            .replace(/^## (.+)$/gm, '<h2 class="text-xl font-bold mt-6 mb-3">$1</h2>')
            .replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold mt-6 mb-3">$1</h1>')
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.+?)\*/g, '<em>$1</em>')
            .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc">$1</li>')
            .replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal">$1</li>')
            .replace(/\n\n/g, '</p><p class="mb-3">')
            .replace(/\n/g, '<br>');
        return '<p class="mb-3">' + html + '</p>';
    }

    // Social/other: render bold and italic but keep plain text layout
    html = html
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br>');
    return html;
});
</script>

<template>
    <div class="w-full max-w-2xl">
        <!-- Skeleton loading state -->
        <div v-if="isGenerating && !content" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4 animate-pulse">
            <div class="h-5 bg-gray-200 rounded w-3/4"></div>
            <div class="space-y-2">
                <div class="h-3 bg-gray-200 rounded w-full"></div>
                <div class="h-3 bg-gray-200 rounded w-full"></div>
                <div class="h-3 bg-gray-200 rounded w-5/6"></div>
            </div>
            <div class="h-4 bg-gray-200 rounded w-1/2 mt-4"></div>
            <div class="space-y-2">
                <div class="h-3 bg-gray-200 rounded w-full"></div>
                <div class="h-3 bg-gray-200 rounded w-full"></div>
                <div class="h-3 bg-gray-200 rounded w-4/5"></div>
                <div class="h-3 bg-gray-200 rounded w-full"></div>
                <div class="h-3 bg-gray-200 rounded w-3/4"></div>
            </div>
            <div class="h-4 bg-gray-200 rounded w-2/3 mt-4"></div>
            <div class="space-y-2">
                <div class="h-3 bg-gray-200 rounded w-full"></div>
                <div class="h-3 bg-gray-200 rounded w-5/6"></div>
            </div>
            <p class="text-xs text-gray-400 text-center mt-6">
                <span v-if="generatingStep === 'writing'">
                    <mf-translate domainkey="content-creator:editor.generatingWriting"></mf-translate>
                </span>
                <span v-else>
                    <mf-translate domainkey="content-creator:editor.generating"></mf-translate>
                </span>
            </p>
        </div>

        <!-- Empty state -->
        <div v-else-if="!content && !isEditing" class="flex flex-col items-center justify-center py-32 text-gray-400">
            <svg class="w-16 h-16 mb-4 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
                <polyline points="14,2 14,8 20,8" />
                <line x1="8" y1="13" x2="16" y2="13" />
                <line x1="8" y1="17" x2="12" y2="17" />
            </svg>
            <p class="text-sm">
                <mf-translate domainkey="content-creator:editor.emptyState"></mf-translate>
            </p>
        </div>

        <template v-else>
            <!-- Edit mode -->
            <textarea
                v-if="isEditing"
                :value="editableContent"
                @input="$emit('update:editable-content', $event.target.value)"
                class="w-full min-h-[500px] p-6 bg-white border border-gray-200 rounded-lg text-sm font-mono leading-relaxed focus:ring-2 focus:ring-aiAgentContentCreator focus:border-aiAgentContentCreator resize-y shadow-sm"
            ></textarea>

            <!-- Preview mode -->
            <div v-else class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div v-if="imageUrl" class="relative mb-4">
                    <img
                        :src="imageUrl"
                        alt="Generated image"
                        class="w-full"
                    />
                    <span class="absolute bottom-3 right-3">
                        <button-base
                            @click="downloadImage"
                            :disabled="isDownloadingImage"
                            :state="isDownloadingImage ? 'waiting' : ''"
                            size="sm"
                            style-type="secondary"
                        >
                            <template #icon>
                                <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M12 3v12" />
                                    <path d="m7 10 5 5 5-5" />
                                    <path d="M5 21h14" />
                                </svg>
                            </template>
                            {{ _mft('shared:download') }}
                        </button-base>
                    </span>
                </div>
                <div v-else-if="imageGenerating" class="w-full h-64 rounded-lg mb-4 bg-gray-100 animate-pulse flex items-center justify-center">
                    <mf-spinner-medium class="h-12 w-24 text-gray-400" />
                </div>
                <div
                    v-if="contentType === 'blog' || contentType === 'newsletter'"
                    class="prose prose-sm max-w-none"
                    v-html="formattedContent"
                ></div>
                <div v-else class="text-sm leading-relaxed text-gray-800"
                    v-html="formattedContent"
                ></div>
            </div>
        </template>
    </div>
</template>
